/**
 * Typed fetch wrapper for the Meridian v2 API.
 *
 * Contract with the backend (docs/design/system-design.md sec 4-5):
 *  - All app data lives under /api/v2. Auth (BFF) lives under /auth.
 *  - Auth is a BFF HttpOnly session cookie (decision #1) — the browser holds NO
 *    bearer token. So every request sends `credentials: "include"` and NEVER an
 *    Authorization header. A 401 means "session invalid" -> bounce to /login.
 *  - List endpoints return the uniform envelope {items, total, next_cursor}.
 *  - org_id/site_id come from the session (g.auth) for non-admin roles, so the
 *    client passes scope as query hints only where the server accepts them
 *    (msp_admin cross-org views); it never trusts them for security.
 */

import type { Paginated } from "./types";

/** Base path — same-origin in prod, proxied in dev (see vite.config.ts). */
const API_BASE = "/api/v2";

/** Error every failed call rejects with. Mirrors the shape of src/ui/js/api.js
 *  so operator-facing error handling stays consistent across the two UIs. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly url: string;
  constructor(message: string, opts: { status: number; body: unknown; url: string }) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status;
    this.body = opts.body;
    this.url = opts.url;
  }
  /** True for transport-level failures (DNS/refused/offline) vs API 4xx/5xx. */
  get isTransport(): boolean {
    return this.status === 0;
  }
}

type QueryValue = string | number | boolean | null | undefined;

/** Build a URL with query params; null/undefined/"" are dropped. */
function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = `${API_BASE}/${path.replace(/^\/+/, "")}`;
  if (!query) return base;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === "") continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Core request. Throws ApiError on any non-2xx or transport failure. */
async function request<T>(
  method: string,
  path: string,
  opts: { query?: Record<string, QueryValue>; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const url = buildUrl(path, opts.query);
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      // BFF session cookie — no bearer token ever touches JS.
      credentials: "include",
      headers: opts.body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    // Network-level: DNS, connection refused, offline, CORS. status 0.
    throw new ApiError((e as Error)?.message || "Network request failed", {
      status: 0,
      body: null,
      url,
    });
  }

  // 204 / empty body — return undefined as T.
  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;

  if (!res.ok) {
    // 401 is special: the caller (queryClient) turns it into a login redirect.
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `${res.status} ${res.statusText}`) || "Request failed";
    throw new ApiError(msg, { status: res.status, body: parsed ?? text, url });
  }
  return parsed as T;
}

/**
 * Multipart POST with UPLOAD PROGRESS. fetch() cannot report request-body progress, and an
 * installer uploading a photo over site LTE needs to see it moving, so this one verb uses
 * XMLHttpRequest. Same contract as request(): same-origin session cookie (never a bearer
 * token), ApiError on non-2xx (with the server's {"error"} text) or on transport failure.
 */
function uploadForm<T>(
  path: string,
  form: FormData,
  opts: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  const url = buildUrl(path);
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    // BFF session cookie. Same-origin already sends it; explicit to match credentials: "include".
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      const text = xhr.responseText;
      const parsed = text ? safeJson(text) : undefined;
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const msg =
        parsed && typeof parsed === "object" && "error" in parsed
          ? String((parsed as { error: unknown }).error)
          : `${xhr.status} ${xhr.statusText}`;
      reject(new ApiError(msg, { status: xhr.status, body: parsed ?? text, url }));
    };
    xhr.onerror = () =>
      reject(new ApiError("Network request failed", { status: 0, body: null, url }));
    xhr.onabort = () => reject(new ApiError("Upload cancelled", { status: 0, body: null, url }));
    opts.signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(form);
  });
}

/** POST returning a file. ApiError (with the server's {"error"} text) on non-2xx. */
async function downloadPost(
  path: string,
  body?: unknown,
): Promise<{ blob: Blob; disposition: string | null }> {
  const url = buildUrl(path);
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",          // BFF session cookie, never a bearer token
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError((e as Error)?.message || "Network request failed", { status: 0, body: null, url });
  }
  if (!res.ok) {
    const text = await res.text();
    const parsed = text ? safeJson(text) : undefined;
    const msg =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(msg, { status: res.status, body: parsed ?? text, url });
  }
  return { blob: await res.blob(), disposition: res.headers.get("Content-Disposition") };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Public client. Thin, typed verbs. Feature hooks (lib/api/hooks.ts) call these;
 * components never call fetch directly.
 */
export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>("GET", path, { query, signal }),
  /** GET a list endpoint returning the {items,total,next_cursor} envelope. */
  list: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<Paginated<T>>("GET", path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body }),
  del: <T>(path: string) => request<T>("DELETE", path),
  /** POST that answers with a FILE (the diagnostics support bundle): returns the body as a
   *  Blob plus the Content-Disposition header. Same cookie-only contract as request(). */
  download: (path: string, body?: unknown) => downloadPost(path, body),
  /** Multipart POST with upload progress (installer photos). */
  upload: <T>(
    path: string,
    form: FormData,
    opts?: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal },
  ) => uploadForm<T>(path, form, opts),
};

/** Kick off the BFF login flow. Full-page navigation (not fetch) so the OIDC
 *  redirect chain runs in the top-level browsing context. */
export function beginLogin(returnTo?: string): void {
  const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "";
  window.location.assign(`/auth/login${q}`);
}

/** Server-side logout (BFF clears the session + back-channel).
 *
 *  /auth/logout is POST-only (a cross-site GET must not be able to force a logout), so we
 *  POST it (same-origin, with the session cookie), then follow the Keycloak end-session URL
 *  it returns for RP-initiated logout -- falling back to the app root. A plain GET navigation
 *  here 405s ("Method Not Allowed"). */
export async function beginLogout(): Promise<void> {
  let target = "/";
  try {
    const res = await fetch("/auth/logout", { method: "POST", credentials: "include" });
    const data = (await res.json().catch(() => ({}))) as { end_session_url?: string | null };
    if (data && data.end_session_url) target = data.end_session_url;
  } catch {
    /* network hiccup — still leave the app (cookie is cleared server-side on success). */
  }
  window.location.assign(target);
}
